import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../api/axiosConfig';

// --- Helper: tạo URL tuyệt đối ---
const createAbsoluteUrl = (path?: string | null) => {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  const base = (api.defaults.baseURL || process.env.REACT_APP_API_URL || '').replace(/\/api\/?$/, '');
  return `${base}${path}`;
};

// --- Interface dữ liệu Cập nhật ---
interface RelatedArticle {
  slug: string;
  // ✅ Update thumbnail object
  thumbnail?: {
    url: string;
    public_id?: string;
  };
  title_vi?: string;
  title_en?: string;
  publishDate?: string;
}

interface ArticleDetail {
  slug: string;
  // ✅ Update thumbnail object
  thumbnail?: {
    url: string;
    public_id?: string;
  };
  title_vi: string;
  title_en: string;
  content_vi: string;
  content_en: string;
  author_vi: string;
  author_en: string;
  category_vi: string;
  category_en: string;
  createdAt: string;
}

// --- Component: Bài viết liên quan ---
const RelatedArticles = ({ currentArticleSlug }: { currentArticleSlug: string }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as 'vi' | 'en';
  const [related, setRelated] = useState<RelatedArticle[]>([]);

  useEffect(() => {
    const fetchRelated = async () => {
      try {
        // Backend trả về list dạng object format mới hoặc array raw (admin=true)
        // Ở public route, thường BE format lại. Cần check kỹ data trả về.
        // Nếu dùng route /news (public), nó trả về { data: [...] }
        const { data } = await api.get(`/news?lang=${lang}`);
        
        // Đảm bảo lấy đúng mảng data
        let list: RelatedArticle[] = [];
        if (data && Array.isArray(data.data)) {
           list = data.data; // Format mới { data: [...], page: ... }
        } else if (Array.isArray(data)) {
           list = data; // Format cũ hoặc raw
        }

        setRelated(list.filter((a) => a.slug !== currentArticleSlug).slice(0, 2));
      } catch (err) {
        console.error('❌ Lỗi khi tải bài viết liên quan:', err);
      }
    };
    fetchRelated();
  }, [lang, currentArticleSlug]);

  if (related.length === 0) return null;

  return (
    <div className="mt-16 pt-8 border-t">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">
        {t('news_detail.related_articles')}
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {related.map((a: RelatedArticle) => {
          // Xử lý title fallback (vì list public có thể trả về title theo lang hiện tại hoặc full object)
          // Ở public API mới: item[`title_${lang}`] đã được map thành `title`
          // Nhưng để an toàn với interface cũ/mới, ta check cả 2
          const title = (a as any).title || (lang === 'vi' ? a.title_vi : a.title_en) || 'Tin tức';
          
          // ✅ Lấy URL thumbnail an toàn
          const thumbUrl = a.thumbnail?.url || '';

          return (
            <Link
              key={a.slug}
              to={`/${lang}/news/${a.slug}`}
              className="group block"
            >
              <div className="overflow-hidden rounded-lg bg-gray-200 h-48">
                {thumbUrl ? (
                  <img
                    src={createAbsoluteUrl(thumbUrl)}
                    alt={title}
                    className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-300"
                  />
                ) : (
                   <div className="w-full h-full flex items-center justify-center text-gray-400">No Image</div>
                )}
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mt-4 group-hover:text-green-700 transition-colors line-clamp-2">
                {title}
              </h3>
              <p className="text-gray-500 text-sm mt-1">
                {a.publishDate ? new Date(a.publishDate).toLocaleDateString('vi-VN') : ''}
              </p>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

// --- Component chính ---
const TinTuc = () => {
  const { articleId } = useParams<{ articleId: string }>();
  const { t, i18n } = useTranslation();
  const lang = i18n.language as 'vi' | 'en';

  const [article, setArticle] = useState<ArticleDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        setLoading(true);
        const { data } = await api.get(`/news/${articleId}`);
        setArticle(data);
        document.title = data[`title_${lang}`] || t('news_page.page_title');
      } catch (err) {
        console.error('❌ Lỗi khi tải bài viết:', err);
        setArticle(null);
      } finally {
        setLoading(false);
      }
    };

    if (articleId) fetchArticle();
    window.scrollTo(0, 0);
  }, [articleId, lang, t]);

  if (loading)
    return (
      <div className="flex justify-center items-center h-screen font-semibold text-lg">
        {t('news_detail.loading')}
      </div>
    );

  if (!article)
    return (
      <div className="flex flex-col items-center justify-center min-h-screen text-center px-4">
        <h1 className="text-4xl font-bold text-gray-800">404</h1>
        <p className="text-xl text-gray-600 mt-2">
          {t('news_detail.not_found_title')}
        </p>
        <Link
          to={`/${lang}/news`}
          className="mt-6 px-6 py-2 bg-green-700 text-white rounded-lg hover:bg-green-800 transition-colors"
        >
          {t('news_detail.view_all_news_button')}
        </Link>
      </div>
    );

  const publishDate = new Date(article.createdAt).toLocaleDateString('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

  return (
    <div className="bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <article>
          {/* --- Header --- */}
          <header className="mb-8 text-center">
            <p className="text-base font-semibold text-green-700 uppercase tracking-wide">
              {article[`category_${lang}`]}
            </p>
            <h1 className="text-3xl md:text-5xl font-extrabold text-gray-900 mt-2 leading-tight">
              {article[`title_${lang}`]}
            </h1>
            <p className="mt-4 text-md text-gray-500">
              {t('news_detail.posted_by')}{' '}
              <span className="font-semibold text-gray-700">
                {article[`author_${lang}`]}
              </span>{' '}
              {t('news_detail.on_date')} {publishDate}
            </p>
          </header>

          {/* --- Ảnh chính --- */}
          {article.thumbnail?.url && (
            <div className="my-10 w-full md:w-5/6 mx-auto bg-gray-100 rounded-lg shadow-lg overflow-hidden">
              <img
                src={createAbsoluteUrl(article.thumbnail.url)}
                alt={article[`title_${lang}`]}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          {/* --- Nội dung HTML (ReactQuill) --- */}
          <div
            className="prose prose-green lg:prose-xl max-w-none mx-auto text-gray-800"
            dangerouslySetInnerHTML={{
              __html: article[`content_${lang}`],
            }}
          />

          {/* --- Bài viết liên quan --- */}
          <RelatedArticles currentArticleSlug={article.slug} />
        </article>
      </div>
    </div>
  );
};

export default TinTuc;